import {
  DetailRequirement,
  EngagementTaskTrait,
  ProgramTask,
  RPDataEntry,
  TaskDataUpdates,
  TraitDisplaysItem,
} from '@cibo/core'
import { Markdown } from '@cibo/ui'
import { Stack } from '@mui/material'
import { propEq } from 'ramda'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useProgramEngagementContext } from '../../../../context'
import { TaskEditorComponentProps } from '../../TaskEditorComponent/types'
import { createYearTokenContext } from './schemaTokenContext'
import { SingleTargetEditorUi } from './SingleTargetEditorUi'
import { SingleTargetSchemaEditor } from './SingleTargetSchemaEditor'

type SingleTargetTraitEditorProps = {
  detailRequirements?: DetailRequirement[]
  taskConfig: ProgramTask<RPDataEntry>
  taskId: string
  traitConfig: EngagementTaskTrait
  resourceId: string
  saveTaskData: (updates: TaskDataUpdates, options?: { onError?: (error: Error) => void }) => void
  traitDisplay?: TraitDisplaysItem
  TaskEditorComponent(props: TaskEditorComponentProps): ReactElement
  SingleTargetTraitDisplays(props: any): ReactElement | ReactElement[] | null
  hideTitles?: boolean
}

export const SingleTargetTraitEditor = ({
  detailRequirements,
  taskConfig,
  taskId,
  traitConfig,
  saveTaskData,
  resourceId,
  traitDisplay,
  TaskEditorComponent,
  SingleTargetTraitDisplays,
  hideTitles,
}: SingleTargetTraitEditorProps) => {
  const { engagementId, engagement, taskData } = useProgramEngagementContext()
  const firstRequirement = detailRequirements?.[0]
  const tokenContext = useMemo(() => createYearTokenContext(firstRequirement?.year), [firstRequirement?.year])
  const traitDetail = taskData.data?.getDetail({ traitConfig, resourceId })
  const ciboSourced = traitDetail?.source === 'cibo'

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  const traitDetailValue =
    traitStatus &&
    taskData.data?.getDetailValue({
      traitConfig: traitStatus,
      detailRequirement: firstRequirement,
      resourceId,
    })
  const [saveError, setSaveError] = useState<Error | undefined>()

  // @TODO: require so we don't have to default
  const resolvedDataType = traitConfig.dataType || 'field'

  /** NOTE: This component encapsulates the translation between field style details
   * and the value-based user & workflow details so that the individual editor components
   * can be agnostic and simply work with the values.
   */
  const handleSaveTraitDetail = useCallback(
    (detail: { value: any; year?: number }) => {
      setSaveError(undefined)

      switch (resolvedDataType) {
        case 'field':
          saveTaskData(
            {
              fieldDetails: [
                {
                  resourceId,
                  details: [
                    {
                      traitId: traitConfig.traitId,
                      result: detail.value,
                      year: firstRequirement?.year,
                    },
                  ],
                },
              ],
            },
            { onError: setSaveError }
          )
          break

        case 'user':
          saveTaskData(
            {
              userDetails: [
                {
                  traitId: traitConfig.traitId,
                  value: detail.value,
                  year: firstRequirement?.year,
                },
              ],
            },
            { onError: setSaveError }
          )
          break

        case 'workflow':
          saveTaskData(
            {
              wfDetails: [
                {
                  traitId: traitConfig.traitId,
                  value: detail.value,
                  year: firstRequirement?.year,
                },
              ],
            },
            { onError: setSaveError }
          )
          break
      }
    },
    [saveTaskData]
  )

  let hideTitle = !!hideTitles
  if (!traitStatus) {
    return null
  }

  if ((traitStatus.schema as any)?.['ui-params']?.hideTitles) {
    hideTitle = true
  }

  // Schema-based yearly trait: render one labeled editor per year in sorted order.
  // Triggered when the trait uses a JSON schema AND the task has multiple yearly
  // detail requirements for it (e.g., 2024 and 2025).
  const yearlySchemaRequirements = detailRequirements?.filter(r => r.year !== undefined)
  if (traitStatus.schema && yearlySchemaRequirements && yearlySchemaRequirements.length > 1) {
    const sorted = [...yearlySchemaRequirements].sort((a, b) => (a.year ?? 0) - (b.year ?? 0))
    return (
      <Stack spacing={1} flex={1} data-testid={`dataEntry-editor-${traitConfig.traitId}`}>
        <Stack>
          <Markdown typographyProps={{ variant: 'h6' }}>{engagement.data?.replaceCopyTokens(traitConfig.title, tokenContext)}</Markdown>
          {!!params?.furtherText && (
            <Markdown style={{ lineHeight: 1.66, fontSize: '0.875rem' }}>
              {engagement.data?.replaceCopyTokens(params.furtherText, tokenContext)}
            </Markdown>
          )}
        </Stack>
        <Stack spacing={3}>
          {sorted.map(req => {
            const yearDetailValue = taskData.data?.getDetailValue({
              traitConfig: traitStatus,
              detailRequirement: req,
              resourceId,
            })
            const handleYearSave = (detail: { value: any }) => {
              setSaveError(undefined)
              switch (resolvedDataType) {
                case 'field':
                  saveTaskData(
                    {
                      fieldDetails: [
                        {
                          resourceId,
                          details: [
                            {
                              traitId: traitConfig.traitId,
                              result: detail.value,
                              year: req.year,
                            },
                          ],
                        },
                      ],
                    },
                    { onError: setSaveError }
                  )
                  break
                case 'user':
                  saveTaskData(
                    {
                      userDetails: [
                        { traitId: traitConfig.traitId, value: detail.value, year: req.year },
                      ],
                    },
                    { onError: setSaveError }
                  )
                  break
                case 'workflow':
                  saveTaskData(
                    {
                      wfDetails: [
                        { traitId: traitConfig.traitId, value: detail.value, year: req.year },
                      ],
                    },
                    { onError: setSaveError }
                  )
                  break
              }
            }
            return (
              <SingleTargetSchemaEditor
                data-testid={`schema-editor-${traitConfig.traitId}-${req.year}`}
                key={req.year}
                traitStatus={traitStatus}
                handleSaveTraitDetail={handleYearSave}
                traitDetailValue={yearDetailValue}
                year={req.year}
              />
            )
          })}
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack spacing={1} flex={1} data-testid={`dataEntry-editor-${traitConfig.traitId}`}>
      <Stack>
        {!hideTitle && <Markdown typographyProps={{ variant: 'h6' }}>{engagement.data?.replaceCopyTokens(traitConfig.title, tokenContext)}</Markdown>}

        {!!params?.furtherText && (
          <Markdown style={{ lineHeight: 1.66, fontSize: '0.875rem' }}>
            {engagement.data?.replaceCopyTokens(params.furtherText, tokenContext)}
          </Markdown>
        )}
      </Stack>

      {engagementId && engagement.data && (
        <SingleTargetEditorUi
          traitStatus={traitStatus}
          detailRequirement={firstRequirement}
          traitDetailValue={traitDetailValue}
          handleSaveTraitDetail={handleSaveTraitDetail}
          traitDetail={traitDetail}
          taskData={taskData}
          taskId={taskId}
          resourceId={resourceId}
          saveError={saveError}
          ciboSourced={ciboSourced}
          resolvedDataType={resolvedDataType}
        />
      )}
      <Stack spacing={2}>
        {traitConfig.children?.map(traitConfig => {
          const childTraitDisplay = traitDisplay?.children?.find(
            propEq('traitId', traitConfig.traitId)
          )

          return (
            <SingleTargetTraitDisplays
              key={traitConfig.traitId}
              taskId={taskId}
              taskConfig={taskConfig}
              traitConfig={traitConfig}
              resourceId={resourceId}
              traitDisplay={childTraitDisplay}
              saveTaskData={saveTaskData}
              TaskEditorComponent={TaskEditorComponent}
              noSection
            />
          )
        })}
      </Stack>
    </Stack>
  )
}
